import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Save, Image as ImageIcon, Pencil } from "lucide-react";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";
import { BackButton } from "../components/BackButton";

type Player = {
  id?: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string; // chemin dans le bucket
};

// --- mini hook i18n "safe" (évite TS2589 & force string) ---
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string => {
    return options != null
      ? String((i18n.t as any)(key, options))
      : String((i18n.t as any)(key));
  };
  return { t, lng };
}

export const EditTeam = () => {
  const { t } = useI18nSafe();

  const [players, setPlayers] = useState<Player[]>([]);
  const [signedUrls, setSignedUrls] = useState<(string | null)[]>([]);
  const [teamName, setTeamName] = useState("");
  const [league, setLeague] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const { id: teamId } = useParams();

  // Charge l'équipe + génère les URLs signées pour les photos existantes
  useEffect(() => {
    const fetchTeam = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        navigate("/login");
        return;
      }

      const { data, error: fetchError } = await supabase
        .from("teams")
        .select("team_name, league, players")
        .eq("id", teamId)
        .single();

      if (fetchError) {
        console.error("Fetch team error:", fetchError);
        setPlayers([]);
        setSignedUrls([]);
        setLoading(false);
        return;
      }

      setTeamName(data?.team_name || "");
      setLeague(data?.league || "");

      const list: Player[] = Array.isArray(data?.players) ? data.players : [];
      setPlayers(list);

      // URLs signées (1h) pour les previews
      const urls = await Promise.all(
        list.map(async (p) => {
          if (!p.photoUrl) return null;
          const { data: urlData, error: urlErr } = await supabase
            .storage
            .from("player-photos")
            .createSignedUrl(p.photoUrl, 3600);
          if (urlErr) {
            console.warn("Signed URL error:", urlErr);
            return null;
          }
          return urlData?.signedUrl ?? null;
        })
      );
      setSignedUrls(urls);
      setLoading(false);
    };

    fetchTeam();
  }, [navigate, teamId]);

  // Helpers
  const setSignedUrlAt = (index: number, url: string | null) => {
    setSignedUrls((prev) => {
      const copy = [...prev];
      copy[index] = url;
      return copy;
    });
  };

  const handleChange = (index: number, field: keyof Player, value: string | number) => {
    setPlayers((prev) => {
      const copy = [...prev];
      // Cast propre pour number
      if (field === "number") {
        const n = Number(value);
        copy[index] = { ...copy[index], number: Number.isFinite(n) ? n : 0 };
      } else {
        copy[index] = { ...copy[index], [field]: value };
      }
      return copy;
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { data: authData } = await supabase.auth.getUser();
    const user = authData?.user;
    if (!user) return;

    const oldPath = players[index].photoUrl; // chemin de l'ancienne photo
    const fileExt = file.name.split(".").pop();
    const safeFirst = (players[index].firstName || "player").replace(/\s+/g, "_");
    const safeLast = (players[index].lastName || "unknown").replace(/\s+/g, "_");
    const fileName = `${safeFirst}-${safeLast}-${Date.now()}.${fileExt}`;
    const filePath = `players/${user.id}/${fileName}`;

    // Supprime l’ancienne photo (non bloquant)
    if (oldPath) {
      const { error: deleteError } = await supabase
        .storage
        .from("player-photos")
        .remove([oldPath]);
      if (deleteError) console.warn("Delete old photo error:", deleteError);
    }

    // Upload
    const { error: uploadError } = await supabase
      .storage
      .from("player-photos")
      .upload(filePath, file, { cacheControl: "3600", upsert: true });

    if (uploadError) {
      alert(t("editteam:upload_error"));
      console.error(uploadError);
      return;
    }

    // Met à jour le chemin + génère l’URL signée pour la preview
    handleChange(index, "photoUrl", filePath);
    const { data: urlData } = await supabase
      .storage
      .from("player-photos")
      .createSignedUrl(filePath, 3600);
    setSignedUrlAt(index, urlData?.signedUrl ?? null);
  };

  const handleSave = async () => {
    const trimmedName = teamName.trim();
    if (!trimmedName) {
      alert(t("editteam:team_name_required"));
      return;
    }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    const { error } = await supabase
      .from("teams")
      .update({ team_name: trimmedName, league: league.trim(), players })
      .eq("id", teamId);

    setSaving(false);

    if (error) {
      alert(t("editteam:save_error"));
      console.error(error);
    } else {
      alert(t("editteam:save_success"));
      navigate("/myteam");
    }
  };

  if (loading) {
    return <LogoLoader />;
  }

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Header mobile sticky */}
      <div className="sticky top-0 z-50 w-full bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 mb-6">
        <div className="relative mx-auto max-w-4xl px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
          {/* Bouton retour */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <BackButton />
          </div>

          {/* Titre centré */}
          <div className="flex items-center justify-center gap-2">
            <Pencil className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h1 className="text-lg font-bold text-orange-600 dark:text-orange-400 text-center">
              {t("editteam:title")}
            </h1>
          </div>
        </div>
      </div>
      {/* Infos équipe : nom + niveau/championnat */}
      <div className="mx-auto max-w-4xl rounded-2xl border bg-white border-gray-200 shadow-sm p-4 mb-6
                       dark:bg-gray-900 dark:border-gray-800">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {t("editteam:team_info_title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.team_name")}</span>
            <input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="border rounded px-2 py-2
                         bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              placeholder={t("editteam:placeholder.team_name")}
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.league")}</span>
            <input
              value={league}
              onChange={(e) => setLeague(e.target.value)}
              className="border rounded px-2 py-2
                         bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              placeholder={t("editteam:placeholder.league")}
            />
          </label>
        </div>
      </div>

      {players.map((player, index) => (
        <div
          key={index}
          className="rounded-2xl border bg-white border-gray-200 shadow-sm p-4 mb-6
                     dark:bg-gray-900 dark:border-gray-800"
        >
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            #{player.number ?? "—"} — {player.firstName || t("editteam:placeholder.first_name")} {player.lastName || t("editteam:placeholder.last_name")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Prénom */}
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.first_name")}</span>
              <input
                value={player.firstName || ""}
                onChange={(e) => handleChange(index, "firstName", e.target.value)}
                className="border rounded px-2 py-2
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editteam:placeholder.first_name")}
              />
            </label>

            {/* Nom */}
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.last_name")}</span>
              <input
                value={player.lastName || ""}
                onChange={(e) => handleChange(index, "lastName", e.target.value)}
                className="border rounded px-2 py-2
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editteam:placeholder.last_name")}
              />
            </label>

            {/* Numéro */}
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.number")}</span>
              <input
                type="number"
                value={player.number ?? 0}
                onChange={(e) => handleChange(index, "number", parseInt(e.target.value || "0", 10))}
                className="border rounded px-2 py-2
                           bg-white border-gray-300 text-gray-900
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                min={0}
              />
            </label>

            {/* Poste */}
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.position")}</span>
              <input
                value={player.position || ""}
                onChange={(e) => handleChange(index, "position", e.target.value)}
                className="border rounded px-2 py-2
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editteam:placeholder.position")}
              />
            </label>

            {/* Photo */}
            <div className="md:col-span-2">
              <span className="block text-sm mb-1 text-gray-700 dark:text-gray-300">{t("editteam:labels.photo")}</span>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded object-cover overflow-hidden bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
                  {signedUrls[index] ? (
                    <img
                      src={signedUrls[index]!}
                      alt={t("editteam:photo_alt", { firstName: player.firstName || "", lastName: player.lastName || "" })}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-gray-500" />
                  )}
                </div>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, index)}
                  className="block text-sm
                             file:mr-3 file:px-3 file:py-2 file:rounded file:border-0
                             file:bg-gray-100 file:text-gray-800
                             hover:file:bg-gray-200
                             dark:file:bg-gray-800 dark:file:text-gray-100 dark:hover:file:bg-gray-700"
                />
              </div>

              {player.photoUrl && (
                <p className="text-xs text-gray-500 mt-1 break-all">
                  {player.photoUrl}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      <div className="text-center">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold
                     focus:outline-none focus:ring-2 focus:ring-orange-400/60
                     ${saving
              ? "bg-orange-300 text-white cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white"}`}
        >
          <Save className="w-4 h-4" />
          {saving ? t("editteam:saving") : t("editteam:save_changes")}
        </button>
      </div>
    </section>
  );
};
